// Cost Explorer — breadcrumb drill-down cluster → namespace → workload.
// At each level: a stacked cost-over-time chart + a sortable breakdown table with
// prev-period Δ badges. Clicking a namespace row descends; the breadcrumb climbs
// back. Uses the /finops/v2 cost/timeseries + cost/breakdown endpoints (the latter
// with a `namespace` filter when drilled in).

import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { ChevronRight, Download, ArrowUpDown, ChevronDown } from 'lucide-react';
import { useCostTimeseries, useCostBreakdown, useFinopsResource } from '../../hooks/useFinopsV2';
import { formatMoney, formatPct, formatCores, formatGB, downloadCsv } from './finopsFormat';
import {
  StatStrip, StatCell, SectionHeader, DeltaBadge, UtilBar, Skeleton, ChDisabledCard, EmptyCard, Segmented,
} from './finopsUi';
import CostTimeseriesChart from './CostTimeseriesChart';

export default function CostExplorer({ clusterId, hours }) {
  // drill: null = cluster (group by namespace); a namespace string = drilled in
  // (group by workload, filtered to that namespace).
  const [drillNs, setDrillNs] = useState(null);
  const [colSort, setColSort] = useState({ key: 'cost_usd', dir: 'desc' });
  // Cluster-level dimension: namespace (drillable) | capacity_type | node.
  // The /finops/v2 endpoints supported these all along; the UI only ever
  // exposed namespace (2026-07-09 industry-grade pass).
  const [dim, setDim] = useState('namespace');

  const groupBy = drillNs ? 'workload' : dim;
  const tsTop = 8;
  const ts = useCostTimeseries(clusterId, hours, groupBy, tsTop);
  const bd = useCostBreakdown(clusterId, hours, groupBy, { top: 200 });
  // when drilled, refetch breakdown/timeseries filtered to the namespace
  const tsDrill = useCostTimeseries(drillNs ? clusterId : null, hours, 'workload', tsTop);
  const bdDrill = useFilteredBreakdown(clusterId, hours, drillNs);

  const activeTs = drillNs ? tsDrill : ts;
  const activeBd = drillNs ? bdDrill : bd;

  if (!activeBd.available) return <ChDisabledCard title="Cost analytics unavailable" />;

  const data = activeBd.data;
  const items = sortItems(data?.items || [], colSort);
  const onHeader = (key) => setColSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  return (
    <div className="space-y-6">
      {/* breadcrumb + dimension switcher */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-sm">
          <button onClick={() => setDrillNs(null)}
                  className={`font-medium ${drillNs ? 'text-blue-600 dark:text-blue-400 hover:underline' : 'text-slate-900 dark:text-white'}`}>
            Cluster
          </button>
          {drillNs && (
            <>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="font-medium text-slate-900 dark:text-white">{drillNs}</span>
            </>
          )}
        </div>
        {!drillNs && (
          <Segmented
            options={[
              { value: 'namespace', label: 'Namespaces' },
              { value: 'capacity_type', label: 'Spot vs On-Demand' },
              { value: 'node', label: 'Nodes' },
            ]}
            value={dim} onChange={setDim}
          />
        )}
      </div>

      {/* capacity / allocated / idle summary — cluster level only */}
      {!drillNs && data && data.capacity_cost_usd != null && (
        <StatStrip cols={6}>
          <StatCell label="Month-to-date" value={formatMoney(data.mtd_cost_usd, { compact: true })}
                    sub="observed since the 1st" />
          <StatCell label="Forecast (EOM)" value={formatMoney(data.forecast_eom_usd, { compact: true })}
                    sub="MTD + run-rate × remaining" />
          <StatCell label="Capacity cost" value={formatMoney(data.capacity_cost_usd, { compact: true })}
                    sub={`provisioned · ${data.window_hours}h`} />
          <StatCell label="Allocated" value={formatMoney(data.allocated_cost_usd, { compact: true })}
                    sub="reserved by requests" />
          <StatCell label="Idle / unallocated" value={formatMoney(data.idle_cost_usd, { compact: true })}
                    sub={`${formatPct(data.idle_pct)} of capacity`}
                    tone="text-amber-600 dark:text-amber-400" />
          <StatCell label="Run-rate (now)" value={formatMoney(
                      data.current_run_rate_monthly != null
                        ? data.current_run_rate_monthly
                        : (data.window_hours > 0 ? (data.capacity_cost_usd / data.window_hours) * 730 : null),
                      { compact: true })}
                    sub={data.current_run_rate_monthly != null
                      ? 'current fleet · matches Node Optimization'
                      : 'window avg × 730h'} />
        </StatStrip>
      )}

      {/* stacked cost-over-time */}
      <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
        <CardContent className="p-5">
          <SectionHeader title={drillNs ? `Cost over time — ${drillNs}` : 'Cost over time'}
                         hint={`by ${groupBy} · ${activeTs.data?.use_rollup ? 'hourly rollup' : 'raw'} · top ${tsTop}`} />
          {activeTs.loading ? <Skeleton className="h-72" />
            : activeTs.data?.series?.some((s) => (s.points || []).some((v) => v != null)) ? (
              <CostTimeseriesChart data={activeTs.data} windowHours={hours} height={300} showForecast />
            ) : <EmptyCard message="No samples in this window yet — data appears within an hour of the agent connecting" />}
        </CardContent>
      </Card>

      {/* breakdown table */}
      <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
        <CardContent className="p-5">
          <SectionHeader title={drillNs ? 'Workloads' : ({ namespace: 'Namespaces', capacity_type: 'Spot vs On-Demand', node: 'Nodes' }[dim] || 'Namespaces')}
                         hint={drillNs ? 'click ← Cluster to go back'
                           : dim === 'namespace' ? 'click a row to drill into workloads' : 'switch to Namespaces to drill into workloads'}>
            <button onClick={() => downloadCsv(`cost-${groupBy}-${clusterId}.csv`, [
              { key: 'key', label: groupBy }, { key: 'cost_usd', label: 'Cost USD' },
              { key: 'prev_cost_usd', label: 'Prev Cost USD' }, { key: 'delta_pct', label: 'Delta %' },
              { key: 'cpu_requested', label: 'CPU Req' }, { key: 'mem_requested_gb', label: 'Mem Req GB' },
              { key: 'efficiency_pct', label: 'Efficiency %' },
            ], items)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 dark:text-text-secondary hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-surface-border rounded-md px-2 py-1">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </SectionHeader>
          {activeBd.loading ? <Skeleton className="h-64" />
            : !items.length ? <EmptyCard message="No allocations in this window" />
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-surface-border">
                      {[
                        { key: 'key', label: drillNs ? 'Workload' : ({ namespace: 'Namespace', capacity_type: 'Capacity type', node: 'Node' }[dim] || 'Namespace'), align: 'left' },
                        { key: 'cost_usd', label: 'Cost', align: 'right' },
                        { key: 'delta_pct', label: 'Δ prev', align: 'right' },
                        { key: 'efficiency_pct', label: 'Efficiency', align: 'right' },
                        { key: 'cpu_requested', label: 'CPU req', align: 'right' },
                        { key: 'mem_requested_gb', label: 'Mem req', align: 'right' },
                      ].map((c) => (
                        <th key={c.key} className={`py-2 px-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                          <button onClick={() => onHeader(c.key)} className={`inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-white ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                            {c.label}<ArrowUpDown className="w-3 h-3 opacity-40" />
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => {
                      const drillable = !drillNs && dim === 'namespace';
                      return (
                        <tr key={r.key}
                            onClick={drillable ? () => setDrillNs(r.key) : undefined}
                            className={`border-b border-slate-100 dark:border-slate-800 ${drillable ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40' : ''}`}>
                          <td className="py-2 px-2">
                            <span className="inline-flex items-center gap-1.5 font-medium text-slate-900 dark:text-white">
                              {drillable && <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                              <span className="truncate max-w-[260px]">{r.key}</span>
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums text-slate-900 dark:text-white">{formatMoney(r.cost_usd)}</td>
                          <td className="py-2 px-2 text-right"><div className="flex justify-end"><DeltaBadge deltaUsd={r.delta_usd} deltaPct={r.delta_pct} /></div></td>
                          <td className="py-2 px-2"><div className="flex justify-end"><UtilBar pct={r.efficiency_pct} tint="bg-emerald-500" /></div></td>
                          <td className="py-2 px-2 text-right tabular-nums text-slate-600 dark:text-text-secondary">{formatCores(r.cpu_requested)}</td>
                          <td className="py-2 px-2 text-right tabular-nums text-slate-600 dark:text-text-secondary">{formatGB(r.mem_requested_gb)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

// breakdown filtered to a namespace (workload level). Its own hook so it only
// fires when drilled in (path is null → the fetcher no-ops).
function useFilteredBreakdown(clusterId, hours, namespace) {
  return useFinopsResource(namespace ? `/clusters/${clusterId}/cost/breakdown` : null,
    { params: { hours, group_by: 'workload', namespace, top: 200 }, enabled: !!namespace });
}

function sortItems(items, { key, dir }) {
  const out = items.slice();
  out.sort((a, b) => {
    const av = a[key], bv = b[key];
    if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return dir === 'asc' ? (av ?? 0) - (bv ?? 0) : (bv ?? 0) - (av ?? 0);
  });
  return out;
}
